/**
 * adminClient.js
 * --------------
 * Centralizes every backend call the admin portal makes — mirrors the
 * teacher portal's data/sheetsClient.js pattern. Every admin page component
 * used to call fetch() directly and re-implement the same
 * credentials/error-handling boilerplate; this is the one place that does it.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Every admin-authenticated call goes through here. If the session has
// expired (401), send the browser straight into the Google OAuth flow —
// same as ProtectedRoute's directLoginPortal does on first load — instead of
// letting the caller proceed with a "Not authenticated" error that every
// section would otherwise have to surface as its own error banner. This is a
// full navigation (not a client-side route change), so it can't loop: the
// backend's OAuth callback lands back on /admin/ with a fresh session.
//
// Returning a promise that never resolves in that case is deliberate: a real
// navigation is already underway, so nothing downstream (parseJsonOrThrow,
// a section's own .catch) should ever get the chance to flash the old
// "Not authenticated" banner in the moment before the browser actually
// leaves the page.
async function adminFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, { ...options, credentials: 'include' });
  if (res.status === 401) {
    window.location.href = `${API_BASE}/api/glogin?portal=admin`;
    return new Promise(() => {});
  }
  return res;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// Every call goes through this — parses the JSON body and, on a non-OK
// response, throws using the backend's own `error` message when present
// (falling back to a generic one) rather than swallowing it.
async function parseJsonOrThrow(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Server error ${res.status}`);
  return body;
}

// Short-TTL cache for the "read the whole tab" GETs, mirroring the backend's
// own read cache (see makeReadCache in backend/index.js, added after these
// same reads burned through Google Sheets' quota). Every one of these
// resources is independently re-fetched by 2-5 sibling admin pages (e.g. the
// four Registration subsections all fetch event-registrations/participants/
// entries/staff/prices), and each is a separate mount — switching tabs
// unmounts one and mounts the next, re-triggering its fetch — so without
// this, browsing between tabs re-hits the backend for data that's almost
// always still current. `get` also de-dupes concurrent callers (e.g. two
// sibling components mounting in the same tick, or React StrictMode's
// double-invoke in dev): whoever asks first triggers the fetch, everyone
// else awaits that same in-flight promise. `invalidate` is called by every
// mutation that writes to the cached resource, so a write is always visible
// on the next read regardless of TTL.
function makeCache(ttlMs) {
  let cached = null;
  let expiresAt = 0;
  let pending = null;
  return {
    async get(fetchFn) {
      const now = Date.now();
      if (cached !== null && now < expiresAt) return cached;
      if (pending) return pending;
      pending = fetchFn()
        .then(data => {
          cached = data;
          expiresAt = Date.now() + ttlMs;
          pending = null;
          return data;
        })
        .catch(err => {
          pending = null;
          throw err;
        });
      return pending;
    },
    invalidate() {
      cached = null;
      expiresAt = 0;
      pending = null;
    },
  };
}

const CACHE_TTL = 15 * 1000;
const pendingSchoolsCache = makeCache(CACHE_TTL);
const approvedSchoolsCache = makeCache(CACHE_TTL);
const eventsCache = makeCache(CACHE_TTL);
const eventRegistrationsCache = makeCache(CACHE_TTL);
const timelineCache = makeCache(CACHE_TTL);
const pricesCache = makeCache(CACHE_TTL);
const usersCache = makeCache(CACHE_TTL);
const feePaymentsCache = makeCache(CACHE_TTL);
const participantsCache = makeCache(CACHE_TTL);
const entriesCache = makeCache(CACHE_TTL);
const staffCache = makeCache(CACHE_TTL);
// The report list only changes when the backend is redeployed.
const printReportsCache = makeCache(60 * 60 * 1000);

export const adminClient = {
  // ---- SESSION ----
  // Resolves to null on any failure (including a non-OK response) rather
  // than throwing — callers treat "no user yet" the same as "not logged in",
  // not an error to surface.
  async getCurrentUser() {
    try {
      const res = await adminFetch('/api/user');
      return res.ok ? res.json() : null;
    } catch {
      return null;
    }
  },

  logout() {
    window.location.href = `${API_BASE}/api/logout`;
  },

  // ---- SCHOOL ACCOUNTS (pending / approved) ----
  async getPendingSchools() {
    return pendingSchoolsCache.get(() => adminFetch('/api/admin/pending-schools').then(parseJsonOrThrow));
  },

  async getApprovedSchools() {
    return approvedSchoolsCache.get(() => adminFetch('/api/admin/approved-schools').then(parseJsonOrThrow));
  },

  async approveSchool(rowIndex) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/approve-school', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ rowIndex }),
    }));
    pendingSchoolsCache.invalidate();
    approvedSchoolsCache.invalidate();
    return result;
  },

  async regeneratePassword(schoolId) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/regenerate-password', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ schoolId }),
    }));
    approvedSchoolsCache.invalidate();
    return result;
  },

  async updateSchoolContact(schoolId, { registrantName, registrantEmail }) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/approved-schools/${encodeURIComponent(schoolId)}/contact`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify({ registrantName, registrantEmail }),
    }));
    approvedSchoolsCache.invalidate();
    return result;
  },

  // Renames the school everywhere its name links records (account, event
  // registration, entries, fee payments, Most Inspiring Dancer) — see the
  // backend route. Returns { schoolName, updated: { [tab]: count } }.
  async renameSchool(schoolId, schoolName) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/approved-schools/${encodeURIComponent(schoolId)}/name`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify({ schoolName }),
    }));
    approvedSchoolsCache.invalidate();
    eventRegistrationsCache.invalidate();
    entriesCache.invalidate();
    participantsCache.invalidate();
    staffCache.invalidate();
    feePaymentsCache.invalidate();
    return result;
  },

  // ---- EVENTS ----
  async getEvents() {
    return eventsCache.get(() => adminFetch('/api/admin/events').then(parseJsonOrThrow));
  },

  async createEvent(form) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/events', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(form),
    }));
    eventsCache.invalidate();
    return result;
  },

  async updateEvent(rowIndex, form) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/events/${rowIndex}`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify(form),
    }));
    eventsCache.invalidate();
    return result;
  },

  // ---- EVENT REGISTRATIONS (one school's registration for an event) ----
  async getEventRegistrations() {
    return eventRegistrationsCache.get(() => adminFetch('/api/admin/event-registrations').then(parseJsonOrThrow));
  },

  async createEventRegistration(formData) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/event-registrations', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(formData),
    }));
    eventRegistrationsCache.invalidate();
    return result;
  },

  async updateEventRegistration(rowIndex, formData) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/event-registrations/${rowIndex}`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify(formData),
    }));
    eventRegistrationsCache.invalidate();
    return result;
  },

  // deleteRoster: also delete the school's participants and entries (see the
  // backend route). expectedSchoolName lets the backend refuse if rows have
  // shifted since this list was loaded.
  async deleteEventRegistration(rowIndex, { deleteRoster = false, expectedSchoolName = '' } = {}) {
    const params = new URLSearchParams({ deleteRoster: String(deleteRoster), expectedSchoolName });
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/event-registrations/${rowIndex}?${params}`, { method: 'DELETE' }));
    eventRegistrationsCache.invalidate();
    if (deleteRoster) {
      participantsCache.invalidate();
      entriesCache.invalidate();
    }
    return result;
  },

  // participantsLockOverride/entriesLockOverride ('' | 'Locked' | 'Open') save
  // through updateEventRegistration above, same as every other field on the
  // edit dialog — this is read-only, just to seed the lock switches' initial
  // display. { participantsLocked, entriesLocked } — the resolved *current*
  // state (this school's override → event-wide override → Timeline deadline),
  // not just which override is set.
  async getRegistrationLocks(rowIndex) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/event-registrations/${rowIndex}/locks`));
  },

  // ---- TIMELINE ----
  async getTimeline() {
    return timelineCache.get(() => adminFetch('/api/admin/timeline').then(parseJsonOrThrow));
  },

  async createTimelineItem(payload) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/timeline', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    }));
    timelineCache.invalidate();
    return result;
  },

  async updateTimelineItem(rowIndex, payload) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/timeline/${rowIndex}`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    }));
    timelineCache.invalidate();
    return result;
  },

  async deleteTimelineItem(rowIndex) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/timeline/${rowIndex}`, { method: 'DELETE' }));
    timelineCache.invalidate();
    return result;
  },

  async reorderTimeline(order) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/timeline/reorder', {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify({ order }),
    }));
    timelineCache.invalidate();
    return result;
  },

  // ---- PRICES ----
  async getPrices() {
    return pricesCache.get(() => adminFetch('/api/admin/prices').then(parseJsonOrThrow));
  },

  async addPrice(item, price) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/prices', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ item, price }),
    }));
    pricesCache.invalidate();
    return result;
  },

  async updatePrice(rowIndex, price) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/prices/${rowIndex}`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify({ price }),
    }));
    pricesCache.invalidate();
    return result;
  },

  async deletePrice(rowIndex) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/prices/${rowIndex}`, { method: 'DELETE' }));
    pricesCache.invalidate();
    return result;
  },

  // ---- USERS (Account Management) ----
  async getUsers() {
    return usersCache.get(() => adminFetch('/api/admin/users').then(parseJsonOrThrow));
  },

  async addUser(user) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/users', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(user),
    }));
    usersCache.invalidate();
    return result;
  },

  async updateUser(rowIndex, user) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/users/${rowIndex}`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify(user),
    }));
    usersCache.invalidate();
    return result;
  },

  async deleteUser(rowIndex) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/users/${rowIndex}`, { method: 'DELETE' }));
    usersCache.invalidate();
    return result;
  },

  // ---- FEE / PAYMENT TRACKING (School Payments page) ----
  async updateSchoolFee(rowIndex, fields) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/event-registrations/${rowIndex}/school-fee`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(fields),
    }));
    eventRegistrationsCache.invalidate();
    // Paying the fee / OK to Proceed changes which rows are paymentPending.
    participantsCache.invalidate();
    entriesCache.invalidate();
    staffCache.invalidate();
    return result;
  },

  // id: participantId (not rowIndex) — the backend resolves the row fresh by
  // this stable ID at write time, so a concurrent teacher-side add/delete
  // can't misdirect the write onto the wrong participant.
  async updateParticipantFee(id, fields) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/participants/${id}/fee`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(fields),
    }));
    participantsCache.invalidate();
    return result;
  },

  // id: staffId (not rowIndex) — same reasoning as updateParticipantFee.
  async updateStaffFee(id, fields) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/staff/${id}/fee`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(fields),
    }));
    staffCache.invalidate();
    return result;
  },

  // ---- FEE PAYMENTS (unified transaction ledger — all 4 fee categories) ----
  async getFeePayments() {
    return feePaymentsCache.get(() => adminFetch('/api/admin/fee-payments').then(parseJsonOrThrow));
  },

  // entryTitle/participantId: optional — scope an 'entry'-category payment to
  // one entry and (within that) one dancer; also used by category: 'credit'
  // (participantId only). See feePaymentLedger.js for how these get read back.
  async addFeePayment({ schoolName, category, amount, paidBy, note, entryTitle, participantId }) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/fee-payments', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ schoolName, category, amount, paidBy, note, entryTitle, participantId }),
    }));
    feePaymentsCache.invalidate();
    return result;
  },

  async deleteFeePayment(rowIndex) {
    const result = await parseJsonOrThrow(await adminFetch(`/api/admin/fee-payments/${rowIndex}`, { method: 'DELETE' }));
    feePaymentsCache.invalidate();
    return result;
  },

  // ---- PARTICIPANT / ENTRY SCREENING (admin-wide, every school) ----
  async getParticipants() {
    return participantsCache.get(() => adminFetch('/api/admin/participants').then(parseJsonOrThrow));
  },

  async getEntries() {
    return entriesCache.get(() => adminFetch('/api/admin/entries').then(parseJsonOrThrow));
  },

  async getStaff() {
    return staffCache.get(() => adminFetch('/api/admin/staff').then(parseJsonOrThrow));
  },

  // { download: true } gets a presigned URL with Content-Disposition:
  // attachment set (forces a save dialog) instead of one meant for inline
  // <audio> playback.
  async getEntryMusicUrl(schoolName, title, { download = false } = {}) {
    const params = new URLSearchParams({ school: schoolName, title });
    if (download) params.set('download', '1');
    return parseJsonOrThrow(await adminFetch(`/api/admin/entries/music-url?${params}`));
  },

  openEntriesMusicZip() {
    window.open(`${API_BASE}/api/admin/entries/music.zip`, '_blank');
  },

  // ---- WORKSHOPS (backend/workshops.js) ----
  // { eventName, leaders: [{ engagementId, leaderId, status, allowedSessions,
  //   compensation, compensationNotes, contractStatus, linkToken,
  //   profile: { name, email, phone, style, bio, adminNotes, photoUrl },
  //   workshops: [...] }], equipmentOptions }
  async getWorkshopLeaders() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/leaders'));
  },
  // Every saved leader profile (for re-inviting returning leaders).
  async getWorkshopProfiles() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/profiles'));
  },
  // { leaderId } re-invites a saved profile; { name, email } creates a new one.
  async addWorkshopLeader(body) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/leaders', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }));
  },
  async saveWorkshopLeader(engagementId, { profile, engagement }) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/leaders/${encodeURIComponent(engagementId)}`, {
      method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ profile, engagement }),
    }));
  },
  async removeWorkshopLeader(engagementId) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/leaders/${encodeURIComponent(engagementId)}`, { method: 'DELETE' }));
  },
  async addWorkshop(engagementId, workshop) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/leaders/${encodeURIComponent(engagementId)}/workshops`, {
      method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(workshop),
    }));
  },
  async saveWorkshop(workshopId, workshop) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/items/${encodeURIComponent(workshopId)}`, {
      method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(workshop),
    }));
  },
  async deleteWorkshop(workshopId) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/items/${encodeURIComponent(workshopId)}`, { method: 'DELETE' }));
  },
  async saveWorkshopSettings(settings) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/settings', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(settings) }));
  },
  // Leader emails: { senderName, replyTo, placeholders,
  //   templates: [{ id, label, subject, body, builtIn }] }
  async getWorkshopEmailSettings() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/email-settings'));
  },
  async saveWorkshopEmailSettings(settings) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/email-settings', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(settings) }));
  },
  // { to, subject, html, replyTo, senderName } for one leader.
  async previewWorkshopEmail({ engagementId, subject, body }) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/email/preview', {
      method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ engagementId, subject, body }),
    }));
  },
  // Saves a template without sending. No id = create one of your own
  // (needs a label). Returns the updated settings plus savedId.
  async saveWorkshopEmailTemplate({ id, label, subject, body }) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/email/templates', {
      method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ id, label, subject, body }),
    }));
  },
  async deleteWorkshopEmailTemplate(id) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/email/templates/${encodeURIComponent(id)}`, { method: 'DELETE' }));
  },
  // templateId: built-in or own template (edits saved back unless
  // saveTemplate === false); none = one-off, optionally saved as
  // newTemplateName. Returns { sent: [names], skipped: [{ name, reason }], stoppedEarly }
  async sendWorkshopEmail({ templateId, subject, body, engagementIds, saveTemplate, newTemplateName }) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/email/send', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ templateId, subject, body, engagementIds, saveTemplate, newTemplateName }),
    }));
  },
  // Spaces & schedule: { eventName, dancerCount, equipmentOptions, sessions,
  //   rules, spaces, workshops, runs, hasPreviousSpaces }
  async getWorkshopSchedule() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/schedule'));
  },
  // runs: [{ workshopId, session, spaceId, capacityOverride }] — the whole schedule.
  async saveWorkshopSchedule(runs) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/schedule', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ runs }) }));
  },
  async saveScheduleSettings({ sessions, rules }) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/schedule-settings', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ sessions, rules }) }));
  },
  async addWorkshopSpace(space) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/spaces', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(space) }));
  },
  async saveWorkshopSpace(spaceId, space) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/spaces/${encodeURIComponent(spaceId)}`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(space) }));
  },
  async deleteWorkshopSpace(spaceId) {
    return parseJsonOrThrow(await adminFetch(`/api/admin/workshops/spaces/${encodeURIComponent(spaceId)}`, { method: 'DELETE' }));
  },
  async copyPreviousWorkshopSpaces() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/spaces/copy-previous', { method: 'POST' }));
  },
  // Dancer assignments: { eventName, sessions, release: { released, releasedAt, buffer },
  //   taskTitle, deadline, hasTimelineRow, locked, runs: [{ runId, session, title,
  //   leaderName, spaceName, capacity, assigned }], schools: [{ schoolId, schoolName,
  //   dancers, session1, session2, complete, spots: { runId: { spots, taken } } }],
  //   dancers: [{ participantId, firstName, lastName, schoolId, schoolName, session1, session2, stale }] }
  async getWorkshopAssignments() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/assignments'));
  },
  // Returns { release, view } — view is the updated Dancer Assignments data.
  async saveWorkshopRelease({ released, buffer }) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/release', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ released, buffer }) }));
  },
  // Supervisors: { sessions, runs: [{ runId, session, title, leaderName, spaceName, capacity, assigned }],
  //   supervisors: [{ staffId, firstName, lastName, role, schoolId, schoolName, session1, session2, stale }],
  //   schoolsWithout: [schoolName] }
  async getWorkshopSupervisors() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/supervisors'));
  },
  // assignments: [{ staffId, session1, session2 }] — only those changing.
  // Returns { changed, view } — view is the updated Supervisors data.
  async saveWorkshopSupervisors(assignments) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/supervisors', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ assignments }) }));
  },
  // assignments: [{ participantId, session1, session2 }] — only the dancers to change.
  // Returns { changed, view } — view is the updated Dancer Assignments data.
  async saveWorkshopAssignments(assignments) {
    const result = await parseJsonOrThrow(await adminFetch('/api/admin/workshops/assignments', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ assignments }) }));
    participantsCache.invalidate();
    return result;
  },

  // Contracts: { title, body, placeholders }
  async getContractTemplate() {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/contract-template'));
  },
  async saveContractTemplate({ title, body }) {
    return parseJsonOrThrow(await adminFetch('/api/admin/workshops/contract-template', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ title, body }) }));
  },
  // Printable leader reports (this event, minus declined): 'bios' | 'tshirts' | 'dietary' | 'payments'.
  openLeaderReport(type) {
    window.open(`${API_BASE}/api/admin/workshops/leaders-report.pdf?type=${encodeURIComponent(type)}`, '_blank');
  },
  // The leader's contract filled in from the current template (blank signature lines).
  openLeaderContractPdf(engagementId) {
    window.open(`${API_BASE}/api/admin/workshops/leaders/${encodeURIComponent(engagementId)}/contract.pdf`, '_blank');
  },
  openSignedContract(engagementId) {
    window.open(`${API_BASE}/api/admin/workshops/leaders/${encodeURIComponent(engagementId)}/signed-contract`, '_blank');
  },
  // Upload a signed copy received some other way (file = a PDF), or remove it (file = null).
  async setSignedContract(engagementId, file) {
    const base = `/api/admin/workshops/leaders/${encodeURIComponent(engagementId)}`;
    let key = null;
    if (file) {
      const { uploadUrl, key: newKey } = await parseJsonOrThrow(await adminFetch(`${base}/signed-contract-upload-url`, {
        method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ size: file.size }),
      }));
      const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } });
      if (!put.ok) throw new Error('Upload failed');
      key = newKey;
    }
    return parseJsonOrThrow(await adminFetch(`${base}/signed-contract`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ key }) }));
  },
  // Uploads straight to storage, then saves it as the leader's photo.
  // Pass file = null to remove the photo. Returns { photoUrl }.
  async setWorkshopLeaderPhoto(engagementId, file) {
    const base = `/api/admin/workshops/leaders/${encodeURIComponent(engagementId)}`;
    let key = null;
    if (file) {
      const { uploadUrl, key: newKey } = await parseJsonOrThrow(await adminFetch(`${base}/photo-upload-url`, {
        method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ contentType: file.type, size: file.size }),
      }));
      const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!put.ok) throw new Error('Photo upload failed');
      key = newKey;
    }
    return parseJsonOrThrow(await adminFetch(`${base}/photo`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ key }) }));
  },

  // ---- PRINT DIALOG ----
  // [{ id, label, description, supportsSchoolFilter }] — whatever printouts
  // the backend offers (see backend/printReports.js).
  async getPrintReports() {
    return printReportsCache.get(() => adminFetch('/api/admin/print/reports').then(parseJsonOrThrow));
  },

  // One combined PDF of the given printouts, optionally for a single school.
  openPrintPdf(reportIds, schoolName) {
    const params = new URLSearchParams({ reports: reportIds.join(',') });
    if (schoolName) params.set('school', schoolName);
    window.open(`${API_BASE}/api/admin/print.pdf?${params}`, '_blank');
  },

  // ---- MOST INSPIRING DANCER ----
  // { nominations: [{ schoolName, teacherName, participantId, dancerName, reason, updatedAt }],
  //   missing: [schoolName], deadline }
  async getInspiringDancers() {
    return parseJsonOrThrow(await adminFetch('/api/admin/inspiring-dancers'));
  },

  // One nomination per page; pass a schoolName to print just that school's.
  openInspiringDancerPdf(schoolName) {
    const query = schoolName ? `?${new URLSearchParams({ school: schoolName })}` : '';
    window.open(`${API_BASE}/api/admin/inspiring-dancers.pdf${query}`, '_blank');
  },

  // ---- INVOICES ----
  // type: 'school' | 'participants' | 'entries' | 'final'. Mirrors the
  // teacher portal's sheetsClient.openInvoicePdf — a plain top-level nav so
  // the session cookie rides along, same as clicking a same-site link.
  // extraParams (only used by 'final', which needs the computed line items —
  // see SchoolLookupSection's buildFinalInvoiceItems) are appended as-is.
  openInvoicePdf(rowIndex, type, extraParams = {}) {
    const params = new URLSearchParams(extraParams);
    const query = params.toString() ? `?${params}` : '';
    window.open(`${API_BASE}/api/admin/invoices/${rowIndex}/${type}.pdf${query}`, '_blank');
  },
};
